import * as React from 'react';
import { useEffect, useState } from 'react';

import classNames from 'classnames';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { setLocation } from 'js/lib/coursera.redirect';
import { useRetracked } from 'js/lib/retracked';

import { Link } from '@coursera/cds-core';

import EnrollmentChoiceTypes from 'bundles/enroll-common/common/EnrollmentChoiceTypes';
import {
  choiceTypeToHandleSubmitPromise,
  submitEnrollmentPromise,
} from 'bundles/enroll-common/lib/enrollmentChoiceUtils';
import {
  canEnrollThroughFreemium,
  generateImpressionForDecoupleRedirectFromCommitmentScreen,
  isFreemiumModelEnabled,
  redirectToFirstItemOrCourseHome,
  useFreemiumTracking,
} from 'bundles/enroll-common/utils/freemiumModelUtils';
import { useCourseEnrollModalData } from 'bundles/enroll-modal/data/usePageData';
import { useEnrollModalEventing } from 'bundles/enroll-modal/utils/eventingUtils';
import paymentsEpicClient from 'bundles/epic/clients/payments';
import useGuidedCourseNextStepsLazy from 'bundles/unified-description-page-common/hooks/useGuidedCourseNextStepsLazy';

import _t from 'i18n!nls/enroll';

import 'css!./__styles__/SubscriptionFooter';

type PropsFromCaller = {
  removeStyles?: boolean;
  message?: string;
};

const SubscriptionFooter = ({ removeStyles, message }: PropsFromCaller) => {
  const { trackEnrollModalContinue } = useEnrollModalEventing();
  const { course, courseTypeMetadataWithVersion, enrollmentAvailableChoices } = useCourseEnrollModalData();
  const { canAuditCourse } = enrollmentAvailableChoices;
  const track = useRetracked();
  const { trackPreviewLinkClick, trackPreviewLinkView } = useFreemiumTracking();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEnrollingThroughFreemium = canEnrollThroughFreemium({
    courseId: course.id,
    enrollmentAvailableChoices,
    courseTypeMetadataWithVersion,
  });

  const { loading: isLoadingNextStep, fetchNextSteps } = useGuidedCourseNextStepsLazy();

  useEffect(() => {
    paymentsEpicClient.get('auditCopyChangeEnabled');
  }, []);

  useEffect(() => {
    if (isEnrollingThroughFreemium) {
      trackPreviewLinkView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnrollingThroughFreemium]);

  if (!canAuditCourse) {
    return null;
  }

  const redirectToCourseHome = () => {
    const courseSlug = course?.slug;

    if (courseSlug) {
      return setLocation(`/learn/${courseSlug}/home/welcome`);
    }
    return setLocation('/');
  };

  const onClickAuditCourse = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (isLoadingNextStep || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    if (isEnrollingThroughFreemium) {
      trackPreviewLinkClick();
      generateImpressionForDecoupleRedirectFromCommitmentScreen();
    }

    track({
      trackingName: 'enroll_subscribe_audit',
      action: 'click',
      trackingData: {
        courseId: course?.id,
      },
    });

    const enrollmentChoiceType = EnrollmentChoiceTypes.AUDIT_COURSE;
    trackEnrollModalContinue(enrollmentChoiceType);
    const handleSubmitPromise = choiceTypeToHandleSubmitPromise[enrollmentChoiceType];
    const options = {
      courseId: course.id,
      isFreemiumLearner: isEnrollingThroughFreemium,
    };

    submitEnrollmentPromise({ handleSubmitPromise, options })
      .then(() => {
        if (!isEnrollingThroughFreemium) {
          return redirectToCourseHome();
        }

        return redirectToFirstItemOrCourseHome({
          courseSlug: course?.slug,
          fetchNextSteps,
        });
      })
      .catch(() => {
        setIsSubmitting(false);
      });
  };

  const footerClasses = classNames('rc-SubscriptionFooter', {
    subscriptionFooter: !removeStyles,
  });

  const isButtonDisabled = isLoadingNextStep || isSubmitting;

  return (
    <div className={footerClasses} data-testid="subscription-footer">
      <p className="body-1-text">
        <FormattedMessage
          // eslint-disable-next-line no-restricted-syntax
          message={message || _t('{audit} this course')}
          audit={
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <Link
              component="button"
              onClick={onClickAuditCourse}
              disabled={isButtonDisabled}
              data-testid="subscription-footer-audit-button"
            >
              {paymentsEpicClient.preview('auditCopyChangeEnabled') || isFreemiumModelEnabled(course.id)
                ? _t('Preview')
                : _t('Audit')}
            </Link>
          }
        />
      </p>
    </div>
  );
};

export default SubscriptionFooter;
